import { Edge, Node } from 'reactflow';
import { ACTION_TO_TYPE } from '../components/run/globalConnections';

export type WorkflowIssueSeverity = 'error' | 'warning';

export type WorkflowIssue = {
   id: string;
   nodeId: string;
   nodeIndex: string;
   nodeName: string;
   action: string;
   severity: WorkflowIssueSeverity;
   field: string;
   message: string;
   /** Short, actionable guidance shown under the message. */
   hint?: string;
};

type ProjectLike = {
   globalConnections?: { [connectionType: string]: string };
   globalGoogleSpreadsheet?: { spreadsheet_id?: string; spreadsheet_name?: string } | null;
} | null | undefined;

const SKIP_ACTIONS = new Set([
   'output', 'merge', 'note', 'comment', 'delay', 'wait', 'loop',
]);

const TRIGGER_ACTIONS = new Set([
   'start', 'webhook',
   'whatsappListener', 'whatsappCloudTrigger', 'metaListenerTrigger',
   'shopifyTrigger', 'odooTrigger', 'easyOrdersTrigger', 'bostaTrigger', 'woocommerceTrigger',
   'sqlTrigger', 'googleDriveTrigger', 'googleCalendarTrigger', 'slackTrigger',
   'typeformTrigger', 'googleFormTrigger', 'outlookTrigger', 'imapTrigger', 'gmailTrigger',
]);

const NATURAL_TERMINALS = /^(output|whatsappSendMessage|whatsappCloudSendMessage|gmail|outlook|twilioSendSms|instagramSend|instaSendMessage|slack)$/;

/** Nodes that need an explicit `request` / operation selected before they can do useful work. */
const REQUEST_REQUIRED_ACTIONS = new Set([
   'spreadsheet', 'airtable', 'gmail', 'googleDrive', 'googleCalendar', 'slack',
   'notion', 'trello', 'clickup', 'jira', 'shopify', 'odoo', 'erpnext',
   'woocommerce', 'supabase', 'sql', 'bigQuery', 'outlook', 'wordpress', 'wordPress',
]);

/** Send / terminal nodes — they send a message, they do not pick a Request operation. */
const NO_REQUEST_OPERATION_ACTIONS = new Set([
   'whatsappSendMessage',
   'whatsappCloudSendMessage',
   'instagramSend',
   'instaSendMessage',
   'twilioSendSms',
   'messengerSendMessage',
   'facebookPosting',
   'instagramPosting',
   'customAPI',
   'rss',
]);

const isPlaceholder = (value?: string | null) => /^<<.+>>$/.test(String(value || '').trim());

const isEmpty = (value: any): boolean => {
   if (value === undefined || value === null) return true;
   if (typeof value === 'string') return !value.trim() || isPlaceholder(value);
   if (Array.isArray(value)) return value.length === 0;
   return false;
};

const getConnId = (value: any): string =>
   String(value?.connectionId || value?.connection?.id || value?.connection?._id || '').trim();

const isUnknownConnection = (value: any): boolean => {
   const conn = value?.connection;
   if (!conn || typeof conn !== 'object') return false;
   const label = String(conn.name || conn.title || conn.label || conn.email || '').trim().toLowerCase();
   if (label === 'unknown' || label === 'unknown connection') return true;
   const id = String(conn.id || conn._id || value?.connectionId || '').trim();
   return !id;
};

const isPendingConnection = (value: any): boolean => {
   const status = String(value?.connection?.status || '').trim().toLowerCase();
   return status === 'pending' || status === 'disconnected' || status === 'error';
};

const hasNodeOrGlobalConnection = (
   action: string,
   value: any,
   project: ProjectLike
): boolean => {
   if (getConnId(value)) return true;
   const type = ACTION_TO_TYPE[action];
   if (type && project?.globalConnections?.[type]) return true;
   return false;
};

const hasSpreadsheetDocument = (value: any, project: ProjectLike): boolean => {
   const sid = String(value?.spreadsheet_id || '').trim();
   if (sid && !isPlaceholder(sid)) return true;
   const globalId = String(project?.globalGoogleSpreadsheet?.spreadsheet_id || '').trim();
   return Boolean(globalId);
};

const hasSheetName = (value: any): boolean => {
   const name = String(value?.sheet_name || '').trim();
   return Boolean(name) && !isPlaceholder(name);
};

const connectionLabel = (action: string): string => {
   const type = ACTION_TO_TYPE[action];
   if (!type) return 'connection';
   if (type === 'whatsapp') return 'WhatsApp connection';
   if (type === 'whatsappCloud') return 'WhatsApp Cloud connection';
   if (type === 'google') return 'Google connection';
   return `${type} connection`;
};

function pushIssue(
   issues: WorkflowIssue[],
   node: Node<any>,
   field: string,
   message: string,
   severity: WorkflowIssueSeverity = 'error',
   hint?: string
) {
   issues.push({
      id: `${node.id}:${field}`,
      nodeId: node.id,
      nodeIndex: String(node.data?.index || node.id),
      nodeName: String(node.data?.name || node.data?.index || node.data?.action || 'Node'),
      action: String(node.data?.action || ''),
      severity,
      field,
      message,
      hint,
   });
}

function collectGraphStructureIssues(nodes: Node<any>[], edges: Edge[], issues: WorkflowIssue[]) {
   if (!nodes.length) {
      return;
   }

   const nodeById = new Map(nodes.map((n) => [n.id, n]));
   const incoming = new Map<string, number>();
   const outgoing = new Map<string, number>();
   for (const n of nodes) {
      incoming.set(n.id, 0);
      outgoing.set(n.id, 0);
   }
   for (const e of edges) {
      if (!nodeById.has(e.source) || !nodeById.has(e.target)) continue;
      outgoing.set(e.source, (outgoing.get(e.source) || 0) + 1);
      incoming.set(e.target, (incoming.get(e.target) || 0) + 1);
   }

   const triggers = nodes.filter((n) => TRIGGER_ACTIONS.has(String(n.data?.action || '')));
   const entryNodes = nodes.filter((n) => {
      const action = String(n.data?.action || '');
      if (!action || action === 'note' || action === 'comment' || action === 'output') return false;
      return TRIGGER_ACTIONS.has(action) || (incoming.get(n.id) || 0) === 0;
   });

   if (!entryNodes.length) {
      const first = nodes[0];
      pushIssue(
         issues,
         first,
         'trigger',
         'No entry point found. The workflow has nowhere to start.',
         'error',
         'Add a Listener, Webhook, or Start node — or connect a flow from a root node.'
      );
   } else if (!triggers.length) {
      pushIssue(
         issues,
         entryNodes[0],
         'trigger',
         'No dedicated trigger/Start node. Run will start from the first root node(s).',
         'warning',
         'Optional: add a Listener / Start so entry points stay clear when the graph grows.'
      );
   }

   if (entryNodes.length > 4) {
      pushIssue(
         issues,
         entryNodes[0],
         'many_entries',
         `${entryNodes.length} separate entry points. Manual Run may ask which one to start, and live traffic can fan out.`,
         'warning',
         'Keep only the branches you need active. Disconnect unused roots to reduce accidental runs.'
      );
   }

   const waListeners = nodes.filter((n) =>
      ['whatsappListener', 'whatsappCloudTrigger'].includes(String(n.data?.action || ''))
   );
   if (waListeners.length > 1) {
      pushIssue(
         issues,
         waListeners[0],
         'duplicate_wa_listener',
         `${waListeners.length} WhatsApp listeners in one workflow. One inbound message can start multiple branches.`,
         'warning',
         'Keep a single listener unless you intentionally split traffic (e.g. Cloud vs Baileys).'
      );
   }

   for (const node of nodes) {
      const action = String(node.data?.action || '');
      if (!action || action === 'note' || action === 'comment') continue;

      const ins = incoming.get(node.id) || 0;
      const outs = outgoing.get(node.id) || 0;
      const isEntry = TRIGGER_ACTIONS.has(action) || ins === 0;

      if (!TRIGGER_ACTIONS.has(action) && ins === 0 && outs === 0) {
         pushIssue(
            issues,
            node,
            'graph_orphan',
            'This node is disconnected from the flow.',
            'warning',
            'Connect it with edges, or remove it so it does not clutter the canvas or confuse runs.'
         );
         continue;
      }

      if (
         !TRIGGER_ACTIONS.has(action) &&
         ins > 0 &&
         outs === 0 &&
         !NATURAL_TERMINALS.test(action) &&
         action !== 'aiAgent' &&
         action !== 'routing' &&
         action !== 'filter'
      ) {
         pushIssue(
            issues,
            node,
            'graph_dead_end',
            'This node has no outgoing connection.',
            'warning',
            'Link it to the next step or a terminal action (Send / Output) so the branch completes cleanly.'
         );
      }

      if (
         !isEntry &&
         !['aiAgent', 'code', 'routing', 'filter', 'editFields', 'start'].includes(action) &&
         ACTION_TO_TYPE[action] &&
         ins === 0 &&
         outs > 0
      ) {
         pushIssue(
            issues,
            node,
            'graph_no_input',
            'This node has no incoming edge.',
            'warning',
            'Connect it from a previous step or a trigger so it receives the right input at run time.'
         );
      }
   }
}

/**
 * Scan every canvas node and return configuration + graph issues to fix before Run.
 * - error  → Critical: must fix or the run will fail / skip real work
 * - warning → Performance / hygiene: workflow can still run, but quality or cost suffers
 */
export function collectWorkflowIssues(
   nodes: Node<any>[],
   edges: Edge[],
   project?: ProjectLike
): WorkflowIssue[] {
   const issues: WorkflowIssue[] = [];
   const hasWhatsAppListener = nodes.some((n) =>
      ['whatsappListener', 'whatsappCloudTrigger'].includes(String(n.data?.action || ''))
   );

   collectGraphStructureIssues(nodes, edges, issues);

   for (const node of nodes) {
      const action = String(node.data?.action || '');
      if (!action || SKIP_ACTIONS.has(action)) continue;

      const value = node.data?.value || {};
      const connType = ACTION_TO_TYPE[action];
      const isAgentTool = edges.some(
         (e) => e.target === node.id && e.sourceHandle === 'Tools'
      );

      // Generic connection requirement for known integration nodes
      if (connType && !hasNodeOrGlobalConnection(action, value, project)) {
         pushIssue(
            issues,
            node,
            'connection',
            `${connectionLabel(action)} is missing.`,
            'error',
            `Open this node (or Global Connections) and choose a valid ${connectionLabel(action)}.`
         );
      } else if (connType && isUnknownConnection(value) && !project?.globalConnections?.[connType]) {
         pushIssue(
            issues,
            node,
            'connection',
            `${connectionLabel(action)} is Unknown / not connected.`,
            'error',
            'Pick a real account on the node or set one under Connections in the top bar.'
         );
      } else if (connType && isPendingConnection(value) && !project?.globalConnections?.[connType]) {
         pushIssue(
            issues,
            node,
            'connection_pending',
            `${connectionLabel(action)} is still pending / disconnected.`,
            'error',
            'Reconnect the account, then re-select it on this node so runs can authenticate.'
         );
      }

      // Operation / request option missing (only for nodes that actually have a Request picker)
      if (
         !NO_REQUEST_OPERATION_ACTIONS.has(action) &&
         !isAgentTool &&
         (REQUEST_REQUIRED_ACTIONS.has(action) || Object.prototype.hasOwnProperty.call(value, 'request')) &&
         isEmpty(value.request) &&
         !['whatsappListener', 'whatsappCloudTrigger', 'googleFormTrigger', 'metaListenerTrigger'].includes(action)
      ) {
         pushIssue(
            issues,
            node,
            'request',
            'No operation selected.',
            'error',
            'Open the node settings and choose what this step should do (e.g. Get row, Append row, Send message).'
         );
      }

      // AI Agent
      if (action === 'aiAgent') {
         if (isEmpty(value.model)) {
            pushIssue(
               issues,
               node,
               'model',
               'AI Agent model is not selected.',
               'error',
               'Open the agent and pick a model. Without one, the node cannot reason or call tools.'
            );
         }
         const hasTools = edges.some(
            (e) => e.source === node.id && e.sourceHandle === 'Tools'
         );
         if (!hasTools) {
            pushIssue(
               issues,
               node,
               'tools',
               'AI Agent has no tools connected.',
               'warning',
               'Connect Spreadsheet / WhatsApp / Knowledge / Browser Use / Computer Use on the Tools handle if this agent should take actions.'
            );
         }
         const maxIter = Number(value.maxIterations ?? value._studioConfig?.maxIterations ?? 0);
         if (Number.isFinite(maxIter) && maxIter > 20) {
            pushIssue(
               issues,
               node,
               'max_iterations',
               `AI Agent max iterations is ${maxIter}. High loops increase latency and cost.`,
               'warning',
               'Keep iterations around 8–15 unless you know the agent needs a long tool loop.'
            );
         }
         if (String(value.memory || '') === 'no-memory' && hasWhatsAppListener) {
            pushIssue(
               issues,
               node,
               'memory',
               'Agent memory is off while a WhatsApp listener is present.',
               'warning',
               'Use short conversation memory (e.g. last-5) so follow-up messages stay coherent.'
            );
         }
      }

      // Routing
      if (action === 'routing') {
         const request = String(value.request || '').trim();
         if (!request) {
            pushIssue(
               issues,
               node,
               'request',
               'Routing condition type is not selected.',
               'error',
               'Choose how this branch decides True/False (equals, contains, LLM decide, …).'
            );
         } else if (request === 'jev_decide') {
            const classCount = String(value.value || '').split(/\r?\n/).map((line: string) => line.trim()).filter(Boolean).length;
            if (classCount < 2) {
               pushIssue(
                  issues,
                  node,
                  'value',
                  'Jev needs at least two classes.',
                  'warning',
                  'Put one class per line, such as "charged twice" and "password reset". The chosen label is what the Output shows.'
               );
            }
            const threshold = value.threshold;
            if (threshold !== undefined && threshold !== '' && (Number.isNaN(Number(threshold)) || Number(threshold) < 0 || Number(threshold) > 1)) {
               pushIssue(
                  issues,
                  node,
                  'threshold',
                  'Jev threshold must be a number from 0 to 1.',
                  'error',
                  'Use a confidence such as 0.8. Output-True shows the chosen class only when Jev is at least this sure.'
               );
            }
         } else if (request !== 'llm_decide' && value.value === undefined) {
            pushIssue(
               issues,
               node,
               'value',
               'Routing comparison value is empty.',
               'warning',
               'Set the value this condition should check, or the branch may always take one side.'
            );
         }
      }

      if (action === 'jevClassifier') {
         const classCount = String(value.value || '').split(/\r?\n/).map((line: string) => line.trim()).filter(Boolean).length;
         if (classCount < 2) {
            pushIssue(
               issues,
               node,
               'value',
               'Jev Classifier needs at least two classes.',
               'warning',
               'Put one class per line. The Class output is the label Jev picks.'
            );
         }
         const threshold = value.threshold;
         if (threshold !== undefined && threshold !== '' && (Number.isNaN(Number(threshold)) || Number(threshold) < 0 || Number(threshold) > 1)) {
            pushIssue(
               issues,
               node,
               'threshold',
               'Jev threshold must be a number from 0 to 1.',
               'error',
               'Use a confidence such as 0.8. The Low confidence output fills only when Jev is below this value.'
            );
         }
      }

      // Spreadsheet
      if (action === 'spreadsheet') {
         if (!hasSpreadsheetDocument(value, project)) {
            pushIssue(
               issues,
               node,
               'spreadsheet_id',
               'No Google Spreadsheet document selected.',
               'error',
               'Choose a document on the node, or set a Global Google Spreadsheet under Connections.'
            );
         }
         const request = String(value.request || '').trim();
         if (request && request !== 'create_spreadsheet' && request !== 'agent_decide' && !hasSheetName(value)) {
            pushIssue(
               issues,
               node,
               'sheet_name',
               'Sheet tab name is missing.',
               'error',
               'Select which tab this node should read or write (e.g. Candidates, Events).'
            );
         } else if ((!request || request === 'agent_decide') && !hasSheetName(value) && !isAgentTool) {
            pushIssue(
               issues,
               node,
               'sheet_name',
               'Sheet tab name is missing.',
               'error',
               'Even for flexible reads, pick a default tab so the node knows where to look.'
            );
         }
         if (request === 'get_row' && !isAgentTool && isEmpty(value.search_value) && isEmpty(value.any)) {
            pushIssue(
               issues,
               node,
               'search_value',
               'Get row has no search value. It may scan a large range each run.',
               'warning',
               'Set a search column/value, or narrow the range, to avoid slow full-sheet reads.'
            );
         }
         if (request === 'clear_sheet') {
            pushIssue(
               issues,
               node,
               'clear_sheet',
               'This node clears an entire sheet when it runs.',
               'warning',
               'Double-check the tab name before Run — this can wipe production data.'
            );
         }
      }

      // Knowledge base
      if (action === 'knowledge' || action === 'knowledgeBase') {
         const folders = Array.isArray(value?.folders) ? value.folders : [];
         const files = Array.isArray(value?.files) ? value.files : [];
         const activeFiles = files.filter((f: any) => f?.isActive !== false && (f?.id || f?._id || f?.fileId));
         if (!folders.length) {
            pushIssue(
               issues,
               node,
               'folders',
               'No knowledge folder selected.',
               'error',
               'Open the node and attach at least one knowledge folder the agent can search.'
            );
         } else if (!activeFiles.length && value?.request !== 'agent_decide') {
            pushIssue(
               issues,
               node,
               'files',
               'Knowledge folder has no active files selected.',
               'warning',
               'Enable the files this agent should use, or switch the operation to Let AI Agent Decide.'
            );
         } else {
            const activeFolder = folders.find((f: any) => f?.isActive && f?.id && f.id !== 'agent_decide');
            const indexingStatus = String(activeFolder?.indexingStatus || '').toLowerCase();
            if (activeFolder && indexingStatus === 'error') {
               pushIssue(
                  issues,
                  node,
                  'indexing',
                  `Knowledge folder "${activeFolder.name || 'selected'}" indexing failed.`,
                  'warning',
                  'Re-index this folder from Knowledge Base. Until then the agent can list files but search may fail.'
               );
            }
            if (activeFiles.length > 40) {
               pushIssue(
                  issues,
                  node,
                  'files_heavy',
                  `${activeFiles.length} knowledge files are active. Large corpora slow every agent turn.`,
                  'warning',
                  'Keep only the files needed for this workflow, or split by client/topic.'
               );
            }
         }
      }

      // Google Drive folder
      if (action === 'googleDrive' && isEmpty(value?.folder_id) && String(value?.request || '') !== 'agent_decide') {
         pushIssue(
            issues,
            node,
            'folder_id',
            'Google Drive folder is not selected.',
            'error',
            'Pick the folder this node should read from or write to.'
         );
      }

      // Gmail
      if (action === 'gmail' && !isAgentTool) {
         const request = String(value.request || '').trim();
         if (request === 'send_email' && isEmpty(value.to) && isEmpty(value.recipient)) {
            pushIssue(
               issues,
               node,
               'to',
               'Gmail send is missing a recipient email.',
               'warning',
               'Set To / recipient, or map it from an upstream field before Run.'
            );
         }
      }

      // WhatsApp send needs listener in graph
      if (action === 'whatsappSendMessage' && !hasWhatsAppListener && !isAgentTool) {
         pushIssue(
            issues,
            node,
            'listener',
            'WhatsApp Sender needs a WhatsApp Listener in this workflow.',
            'warning',
            'Add a Listener, or connect this sender as an AI Agent tool so replies use the inbound context.'
         );
      }

      if (action === 'whatsappListener' || action === 'whatsappCloudTrigger') {
         const debounce = Number(value?.messageDebounceMs ?? 0);
         if (!Number.isFinite(debounce) || debounce <= 0) {
            pushIssue(
               issues,
               node,
               'debounce',
               'Message debounce is off. Rapid messages each start a full run.',
               'warning',
               'Set debounce to ~2000–3000 ms so short bursts merge into one reply and save cost.'
            );
         }
      }

      // WhatsApp Cloud send needs recipient when used outside agent tools
      if (action === 'whatsappCloudSendMessage') {
         const to = String(value?.to || value?.phoneNumber || '').trim();
         if (!isAgentTool && !to) {
            pushIssue(
               issues,
               node,
               'phoneNumber',
               'WhatsApp Cloud Send needs a recipient phone number.',
               'error',
               'Set phoneNumber / To on the node, or use it as an agent tool to reply to the inbound chat.'
            );
         }
      }

      // BigQuery
      if (action === 'bigQuery' && isEmpty(value?.project_id)) {
         pushIssue(
            issues,
            node,
            'project_id',
            'BigQuery project ID is missing.',
            'error',
            'Enter the GCP project ID this query should run against.'
         );
      }

      // RSS
      if (action === 'rss' && isEmpty(value?.feedUrl || value?.url)) {
         pushIssue(
            issues,
            node,
            'feedUrl',
            'RSS feed URL is missing.',
            'error',
            'Paste the feed URL this node should poll.'
         );
      }

      // Jira
      if (action === 'jira' && isEmpty(value?.project_key)) {
         pushIssue(
            issues,
            node,
            'project_key',
            'Jira project key is missing.',
            'error',
            'Set the project key (e.g. PROJ) used by this Jira action.'
         );
      }

      // Custom API
      if (action === 'customAPI' && isEmpty(value?.url || value?.endpoint)) {
         pushIssue(
            issues,
            node,
            'url',
            'Custom API URL/endpoint is missing.',
            'error',
            'Add the full request URL before running this step.'
         );
      }

      // SQL / Supabase without query when not agent tool
      if ((action === 'sql' || action === 'supabase') && !isAgentTool) {
         const request = String(value.request || value.operation || '').trim();
         if (request && /query|select|execute/i.test(request) && isEmpty(value.query || value.sql || value.statement)) {
            pushIssue(
               issues,
               node,
               'query',
               'SQL query is empty.',
               'error',
               'Write the query this node should execute, or map it from an upstream field.'
            );
         }
      }

      // OCR / vision
      if ((action === 'ocr' || action === 'documentIntelligence') && isEmpty(value?.file_url || value?.url || value?.image_url)) {
         const hasIncoming = edges.some((e) => e.target === node.id);
         if (!hasIncoming) {
            pushIssue(
               issues,
               node,
               'file_url',
               'No file/image input.',
               'warning',
               'Connect an upstream node or set a file URL in options so this step has something to read.'
            );
         }
      }

      // Airtable base/table
      if (action === 'airtable' && !isAgentTool && getConnId(value)) {
         if (isEmpty(value?.base_id || value?.baseId) || isEmpty(value?.table_id || value?.tableId)) {
            pushIssue(
               issues,
               node,
               'airtable_target',
               'Airtable base / table is not selected.',
               'error',
               'After connecting, choose which base and table this node should use.'
            );
         }
      }
   }

   // Deduplicate by id
   const seen = new Set<string>();
   return issues.filter((issue) => {
      if (seen.has(issue.id)) return false;
      seen.add(issue.id);
      return true;
   });
}

export function hasBlockingWorkflowIssues(issues: WorkflowIssue[]): boolean {
   return issues.some((issue) => issue.severity === 'error');
}

export const WORKFLOW_ISSUES_OPEN_EVENT = 'dragify:workflow-issues-open';
export const WORKFLOW_ISSUES_TOGGLE_EVENT = 'dragify:workflow-issues-toggle';
export const WORKFLOW_CONNECTIONS_OPEN_EVENT = 'dragify:open-connections';

export function openWorkflowIssuesPanel() {
   if (typeof window === 'undefined') return;
   window.dispatchEvent(new CustomEvent(WORKFLOW_ISSUES_OPEN_EVENT));
}

export function toggleWorkflowIssuesPanel() {
   if (typeof window === 'undefined') return;
   window.dispatchEvent(new CustomEvent(WORKFLOW_ISSUES_TOGGLE_EVENT));
}

export function openWorkflowConnectionsPanel() {
   if (typeof window === 'undefined') return;
   window.dispatchEvent(new CustomEvent(WORKFLOW_CONNECTIONS_OPEN_EVENT));
}
